import { DataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { UITools } from '@ditto/shared';
import { ColumnDef } from '@tanstack/react-table';
import { Download, FileSpreadsheet, CheckCircle } from 'lucide-react';
import { FC } from 'react';

export type ApplicableStandardsSearchInput =
  UITools['applicableStandardsSearch']['input'];
export type ApplicableStandardsSearchOutput =
  UITools['applicableStandardsSearch']['output'];

export const ApplicableStandardsSearchDisplay: FC<{
  input: ApplicableStandardsSearchInput;
  output: ApplicableStandardsSearchOutput;
}> = ({ input, output }) => {
  const handleDownload = () => {
    window.open(output.signedUrl, '_blank');
  };

  return (
    <FlexCol gap="4" className="w-full">
      {/* Search Summary */}
      <FlexCol gap="2">
        <FlexRow gap="2" alignItems="center">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <Text variant="body" className="font-medium">
            Standards Search Complete
          </Text>
        </FlexRow>
        <Text variant="body-sm" className="text-muted-foreground">
          Searched for: <strong>{input.deviceName}</strong>
          {input.region && ` (${input.region})`}
        </Text>
      </FlexCol>

      {/* Results Summary */}
      <FlexRow gap="4" className="flex-wrap">
        <FlexCol className="bg-primary/5 rounded-lg p-4 min-w-[120px]">
          <Text variant="header2" className="text-primary">
            {output.relevantCount}
          </Text>
          <Text variant="body-sm" className="text-muted-foreground">
            Relevant Standards
          </Text>
        </FlexCol>
        <FlexCol className="bg-muted rounded-lg p-4 min-w-[120px]">
          <Text variant="header2">{output.totalStandards}</Text>
          <Text variant="body-sm" className="text-muted-foreground">
            Total Evaluated
          </Text>
        </FlexCol>
      </FlexRow>

      {/* Download Button */}
      <FlexRow>
        <Button onClick={handleDownload} className="gap-2">
          <Download className="h-4 w-4" />
          Download Full Results (Excel)
        </Button>
      </FlexRow>

      {/* Top Standards Table */}
      {output.topStandards && output.topStandards.length > 0 && (
        <FlexCol gap="2">
          <FlexRow gap="2" alignItems="center">
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
            <Text variant="body" className="font-medium">
              Top Relevant Standards
            </Text>
          </FlexRow>
          <div className="overflow-x-auto max-w-[800px]">
            <DataTable
              columns={topStandardsColumns}
              data={output.topStandards}
            />
          </div>
        </FlexCol>
      )}
    </FlexCol>
  );
};

// Columns for top standards table
const topStandardsColumns: ColumnDef<
  NonNullable<ApplicableStandardsSearchOutput['topStandards']>[number]
>[] = [
  {
    accessorKey: 'designation',
    header: () => (
      <Text variant="body" color="primary">
        Standard
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body" className="font-mono font-medium">
        {row.original.designation}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[120px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[120px]',
    },
  },
  {
    accessorKey: 'title',
    header: () => (
      <Text variant="body" color="primary">
        Title
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body" className="text-neutral-600">
        {row.original.title}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[250px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[250px]',
    },
  },
  {
    accessorKey: 'relevanceScore',
    header: () => (
      <Text variant="body" color="primary">
        Score
      </Text>
    ),
    cell: ({ row }) => {
      const score = row.original.relevanceScore;
      const colorClass =
        score >= 8
          ? 'text-green-600'
          : score >= 5
            ? 'text-yellow-600'
            : 'text-neutral-600';
      return (
        <Text variant="body" className={`font-medium ${colorClass}`}>
          {score}/10
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[80px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[80px]',
    },
  },
  {
    accessorKey: 'reason',
    header: () => (
      <Text variant="body" color="primary">
        Relevance Reason
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body-sm" className="text-neutral-600">
        {row.original.reason}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[300px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[300px]',
    },
  },
];
